/**
 * What a loop is CALLED when nobody has named it — resolved at RENDER time.
 *
 * Authorised by: RULINGS R27, implementation-brief §11 ("literal-language mode covers 100% of
 * shipped strings"), product-concept §7.1 (capture demands nothing — "the loop is written before
 * the question is asked").
 *
 * THE DEFECT THIS EXISTS TO MAKE IMPOSSIBLE. Capture used to write `t('capture.voiceTitle')` into
 * `Loop.title`, so a voice capture taken in the warm register was persisted as the English
 * sentence "A voice line, not written down yet" — and that sentence stayed in the user's archive
 * for ever. Switching to literal mode did not change it, because it was no longer a string the
 * app rendered; it was data. Literal coverage was 100% of the catalogue and less than 100% of
 * what the user actually read, which is the clause §11 is about.
 *
 * So the domain says `title: string | null` (R27), `null` means "nobody has named this yet", and
 * the words come from the catalogue at the moment of drawing, in whichever register the user is in
 * right now. `acceptProposal` is the only transition that turns a `null` into a real title.
 *
 * WHY IT LIVES IN `@carn/copy`, WHICH KNOWS NOTHING ABOUT LOOPS.
 *
 * Because the answer to "what is an unnamed loop called" has to be ONE answer, and every layer
 * needs it: `LoopCard` in `packages/ui`, half a dozen surfaces in `apps/mobile`, and
 * `apps/mobile/src/screens/sorting/proposals.ts`, which is a pure module tested without React
 * Native. There were briefly two implementations in two packages, disagreeing about their own
 * argument order, which is exactly the drift this repository's comments keep warning about — and
 * the version that lived in `@carn/ui` dragged `react-native-reanimated` into a plain
 * Node test run through the package barrel.
 *
 * `@carn/copy` is the leaf: it depends on nothing, so anything may import it. It cannot import
 * `@carn/core` for `Loop` or `CaptureChannel`, and it does not need to — the parameter is
 * STRUCTURAL, which is also what makes this callable with a projection (a toss payload, a
 * shopping row) rather than only with a whole loop.
 */

import type { CopyKey } from './catalogue';

/** Enough of a loop to name it. `Loop` from `@carn/core` satisfies this. */
export type UnnamedCapture = {
  readonly title: string | null | undefined;
  readonly origin: { readonly channel: string };
};

/**
 * Which key names an unnamed capture, selected by the channel it arrived through.
 *
 * A photo capture IS its image (product-concept §7.1, "the broken hinge *is* the task"), so it
 * says so. A share-sheet capture with no words is a thing another app handed over — an image sent
 * straight from Photos is the ordinary case — so it names the gesture, which is the part the user
 * will recognise later. Everything else that can reach here without a typed line arrived through a
 * spoken or otherwise wordless path — `voice`, and the `interrupt` / `walkaround` hold-to-speak
 * surfaces. A channel that cannot produce an unnamed capture at all (`text`, `tossed-to-me` and
 * `ask-reply` all carry words by construction) still resolves, because a defensive `null` must
 * never render as blank: design-direction §4 objection 1's "beautiful forgetting machine" is the
 * risk, and a card with no title is that machine's first symptom.
 *
 * ═══ `share-sheet` USED TO FALL THROUGH TO `capture.voiceTitle`, AND THAT WAS A LIE WAITING ═══
 *
 * This function's own comment used to list `share-sheet` among the channels that "carry words by
 * construction", which was true only while the channel was unbuilt (docs/DEVIATIONS.md DP9 recorded
 * it as designed-for and with "no route onto the device at all"). Building it made photo-only
 * shares reachable, and every one of them would have been captioned "A voice line, not written down
 * yet" — a sentence about an event that never happened, frozen in an append-only archive. The third
 * branch is what closes that, and `capture.sharedTitle`'s catalogue entry carries the argument.
 *
 * The channel vocabulary is `CaptureChannel` in `@carn/core`; it is taken as a string here only
 * because a leaf package may not import the domain.
 *
 * NOTE FOR NATIVE READERS: `WidgetText.standInKey` in
 * `apps/mobile/native/ios/CarnWidgets/Payload.swift` RESTATES this function, because a Swift file
 * cannot import a TypeScript one. `apps/mobile/native/README.md` §3 lists it among the rules
 * duplicated across the language boundary. A branch added here must be added there too.
 */
export function untitledKeyFor(channel: string): CopyKey {
  if (channel === 'photo') return 'capture.photoTitle';
  if (channel === 'share-sheet') return 'capture.sharedTitle';
  return 'capture.voiceTitle';
}

/**
 * The title to draw. Never blank, never persisted.
 *
 * `t` is the translator from `useCopy()`, passed in rather than read from a hook so this stays
 * callable from a projection, a test and a `useMemo` alike.
 */
export function loopTitle(loop: UnnamedCapture, t: (key: CopyKey) => string): string {
  const named = loop.title?.trim();
  return named ? named : t(untitledKeyFor(loop.origin.channel));
}
